import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'

import { withDefaults } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/config'
import { useWordProcessor } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useWordProcessor'

// components
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import { default as WordCloudChart } from '@aytm/app/stats/page/question-views/components/word-cloud/Chart'
import WordCloudPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/WordCloud'

import style from '@aytm/app/stats/page/question-views/info-view/components/charts/oex-cols/styles/Block.module.scss'

// First paint, before the report column is read.
const DEFAULT_WIDTH = 814

const COLUMN_HEIGHT = 500

// The two fixed word-cloud presets for the oex-2-col layout (was wcFormatDefaults in oex_cols.coffee).
const WC_FORMAT_DEFAULTS = {
  green: {
    scaleFunction: 'sqrt',
    angles: [18, 288],
    colors: ['#8CC63F', '#60AC66', '#419982', '#1D83A2', '#1868A6', '#135182', '#003863'],
    rangeStart: 16,
    rangeEnd: 60,
    maxWords: 144,
    orientation: 'v',
    fontFamily: 'Alfa Slab One',
    currentPresetId: '-1'
  },
  red: {
    angles: [18, 288],
    colors: ['#FFFFFF', '#FBB03B', '#F7931E', '#F15A24', '#ED1C24', '#C1272D', '#6E272D'],
    rangeStart: 16,
    rangeEnd: 60,
    maxWords: 144,
    orientation: 'v',
    fontFamily: 'Six Caps',
    currentPresetId: '-1'
  }
}

// One word-cloud column: processes its words and renders the (controls-free) chart. `domId` is
// what the pptx exporter looks the rendered svg up by. A word click narrows the column to that
// word, as an open-ended cloud does; the pick lives with the page, not the report, because the
// column has no report key of its own to hold it under.
const Column = ({ width, title, formatKey, rawData, isLoading, domId }) => {
  const [activeWord, setActiveWord] = useState('')
  const config = useMemo(() => withDefaults({ ...WC_FORMAT_DEFAULTS[formatKey], activeWord }), [formatKey, activeWord])
  const onWordClick = useCallback(({ text }) => setActiveWord(text), [])
  const onParamsChange = useCallback(params => {
    if ('activeWord' in params) setActiveWord(params.activeWord)
  }, [])
  const { words } = useWordProcessor(rawData, config, false)

  return (
    <div className={style.column} style={{ width: `${width}px`, height: `${COLUMN_HEIGHT}px` }}>
      <div className={style.title}>{title}</div>
      <WordCloudPlaceholder height={COLUMN_HEIGHT} isLoading={isLoading}>
        <WordCloudChart
          width={width}
          height={COLUMN_HEIGHT}
          model={{ useRawData: false, id: domId }}
          words={words}
          config={config}
          onParamsChange={onParamsChange}
          onWordClick={onWordClick}
        />
      </WordCloudPlaceholder>
    </div>
  )
}

Column.displayName = 'OexColumn'

Column.propTypes = {
  width: PropTypes.number,
  title: PropTypes.string,
  formatKey: PropTypes.string,
  rawData: PropTypes.object,
  isLoading: PropTypes.bool,
  domId: PropTypes.string
}

// info-view oex-2-col block: two side-by-side word clouds (one per answer variant), no controls.
// Replaces info_view/charts/oex_cols.coffee (OpenEndedExtendedColsView + the OpenEndedExtended
// subitem / OE-block / WcView stack). Two columns because oex-2-col always has two answer variants.
const Block = ({ id, format, answer, answerVariants, onReady }) => {
  // the two clouds split the report column between them, which the hook reads for itself
  const { width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  const columns = useMemo(
    () =>
      (answerVariants || []).map(av => {
        const columnAnswer =
          answer?.[`a${av.position}`] != undefined
            ? answer[`a${av.position}`]
            : ('loading' === answer && 'loading') || { total: 0 }
        return {
          uid: av.uid ?? av.position,
          position: av.position,
          domId: `${id}-${av.position}`,
          title: av.text,
          formatKey: av.format || format?.[`a${av.position}`],
          rawData: ('loading' !== columnAnswer && columnAnswer?.cloudWords) || { words: [] },
          isLoading: 'loading' === columnAnswer
        }
      }),
    [id, format, answer, answerVariants]
  )

  const snapshot = useRef()
  snapshot.current = columns

  // pptx / summary bridge: each column is exported as a cloud of its own, the way any block that
  // owns sub-items answers for them. The exporters read `items` off this.
  const api = useMemo(
    () => ({
      getExportData: () => ({
        items: snapshot.current.map(col => ({
          uid: col.uid,
          position: col.position,
          model: { id: col.domId, viewMode: 'wordcloud' }
        }))
      })
    }),
    []
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const colWidth = width / (columns.length || 1)

  return (
    <BlockWrapper>
      <div className={style.columns}>
        {columns.map((col, i) => (
          <Column key={i} width={colWidth} {...col} />
        ))}
      </div>
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  format: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  onReady: PropTypes.func
}

export default Block
